import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ROUTES } from 'src/app/constants/Routes';
import { BlogService } from 'src/app/pages/blog/services/blog.service';
import { BlogPost } from 'src/app/pages/blog/models/blog-post.model';
import { SeoService } from 'src/app/service/seo.service';


@Component({
  selector: 'app-resources',
  templateUrl: './resources.component.html',
  styleUrls: ['./resources.component.scss'],
  standalone: false
})
export class ResourcesComponent implements OnInit {
  constructor(
    private blogService: BlogService,
    private router: Router,
    private seoService: SeoService
  ) {}
  Routes = ROUTES;
  activeSection = 'docs';
  supportPhone = '9579292320';
  supportEmail = 'vc.support@varroc.com';
  supportAddress = 'Varroc Tech Centre, Varroc Engineering Ltd, Elpro International Ltd., Chafekar Chowk, Chinchwad Gaon, Pimpri-Chinchwad, Pune - 411033.';
  mapsDirectionLink = 'https://maps.app.goo.gl/8TUqnsAMVz7Lr6Yv5';

  sections = [
    // { id: 'casestudies', label: 'Case Studies', icon: 'bx-briefcase' },
    { id: 'docs', label: 'Product Docs', icon: 'bx-file' },
    { id: 'media', label: 'Media', icon: 'bx-play-circle' },
    { id: 'blogs', label: 'Blogs', icon: 'bx-news' },
    { id: 'support', label: 'FAQs & Support', icon: 'bx-support' },
  ];

  caseStudies = [
    {
      industry: 'Fleet Management',
      company: 'Leading Logistics Co. (India)',
      outcome: '32% reduction in fuel costs',
      detail: 'Deployed TCU Pro across 1,200 vehicles. Driver coaching & route optimisation reduced fuel spend by ₹94 lakhs annually.',
      metrics: [{ val: '32%', label: 'Fuel Cost Reduction' }, { val: '1,200', label: 'Vehicles' }, { val: '₹94L', label: 'Annual Savings' }]
    },
    {
      industry: 'Motor Insurance',
      company: 'Regional Insurer (South Asia)',
      outcome: '18% claims reduction with UBI',
      detail: 'Integrated ConnectIQ Driver Score API into underwriting workflow. High-score policyholders showed 18% fewer claims in Year 1.',
      metrics: [{ val: '18%', label: 'Claims Reduction' }, { val: '50K+', label: 'Policyholders' }, { val: '2.1x', label: 'ROI' }]
    },
    {
      industry: 'Automotive OEM',
      company: '2W OEM (India)',
      outcome: 'Connected 800K legacy vehicles',
      detail: 'Retrofit OBD programme enabled a connected car app launch for existing customers — 800K activations in 6 months.',
      metrics: [{ val: '800K', label: 'Connected Vehicles' }, { val: '91%', label: 'App Activation Rate' }, { val: '6 mo', label: 'Time to Launch' }]
    },
    {
      industry: 'Consumer B2C',
      company: 'Varroc Connect Direct',
      outcome: '₹1.2Cr rewards distributed',
      detail: 'Mobility rewards programme drove 35% increase in app DAU and ₹1.2Cr in partner-funded rewards to safe drivers.',
      metrics: [{ val: '35%', label: 'DAU Increase' }, { val: '₹1.2Cr', label: 'Rewards Paid' }, { val: '4.6★', label: 'App Rating' }]
    },
  ];

  docs = [
    { icon: 'bx-book', title: 'Getting Started Guide', desc: 'API keys, SDK setup, and first trip in under 30 minutes.', tag: 'Quick Start' },
    { icon: 'bx-code-alt', title: 'REST API Reference', desc: 'Full OpenAPI specification with request/response examples for all 50+ endpoints.', tag: 'API Docs' },
    { icon: 'bx-mobile-alt', title: 'Mobile SDK Guide', desc: 'iOS and Android integration with background collection, permissions, and testing.', tag: 'Mobile' },
    { icon: 'bx-chip', title: 'Hardware Setup Manuals', desc: 'OBD, TCU, and DashCam installation guides with troubleshooting steps.', tag: 'Hardware' },
    { icon: 'bx-data', title: 'Data Schema Reference', desc: 'Full telemetry event schema, field definitions, and data type documentation.', tag: 'Data' },
    { icon: 'bx-shield-alt-2', title: 'Security & Compliance', desc: 'Authentication, encryption, data handling, and compliance certifications.', tag: 'Security' },
  ];

  supportChannels = [
    { icon: 'bx-envelope', title: 'Email Support', desc: 'Write to our support team for product and technical help.', action: 'vc.support@varroc.com', href: 'mailto:vc.support@varroc.com' },
    { icon: 'bx-phone', title: 'Phone Support', desc: 'Call our support line for quick assistance.', action: '+91 9579292320', href: 'tel:+919579292320' },
  ];

  blogPosts: BlogPost[] = [];
  blogLoading = true;

  openBlog(post: BlogPost): void {
    const slug = post.seo.urlSlug.replace(/^\//, '');
    this.router.navigate(['/blog', slug]);
  }

  viewAllPosts(): void {
    this.router.navigate(['/blog']);
  }

  faqs = [
    { q: 'What vehicles does the Varroc OBD dongle support?', a: 'Our OBD dongle supports all petrol and diesel vehicles with an OBD-II port — typically all cars manufactured after 2008 in India and 1996 in the US/EU. It covers 95% of vehicles on Indian roads.', open: false },
    { q: 'Can I integrate the ConnectIQ APIs into my existing mobile app?', a: 'Yes. Unified SDK provides native iOS (Swift), Android (Kotlin), React Native, Flutter, and JavaScript wrappers. Most integrations go live in 3–5 working days.', open: false },
    { q: 'Is my vehicle data stored securely?', a: 'All data is encrypted in transit (TLS 1.3) and at rest (AES-256). We are ISO 27001 certified and CERT-In compliant. You retain full ownership of your data.', open: false },
    { q: 'What is the difference between the OBD dongle and the TCU?', a: 'The OBD dongle is self-install and ideal for individual vehicles and consumer use. The TCU is hardwired, fleet-grade, AIS-140 certified, and designed for commercial fleets with advanced I/O requirements.', open: false },
    { q: 'How does the driver behaviour score work?', a: 'Our ML models analyse 40+ telematics events per trip — including speeding, harsh braking, cornering, and night driving — normalised by road type and traffic conditions to produce a fair, context-aware score.', open: false },
    { q: 'Can Varroc Connect deploy on-premise?', a: 'Yes. Our enterprise tier supports on-premise deployment in your private cloud (AWS, GCP, Azure) or on-site data centre. Contact our enterprise team for architecture guidance.', open: false },
  ];

  toggleFaq(index: number) {
    this.faqs[index].open = !this.faqs[index].open;
  }

  ngOnInit() {
    this.blogService.getAllBlogPosts().subscribe(posts => {
      this.blogPosts = posts
        .slice()
        .sort((a, b) => b.publishedDate.getTime() - a.publishedDate.getTime())
        .slice(0, 3);
      this.blogLoading = false;
    });

    this.seoService.addJsonLdSchema({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'What vehicles does the Varroc OBD dongle support?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Our OBD dongle supports all petrol and diesel vehicles with an OBD-II port — typically all cars manufactured after 2008 in India and 1996 in the US/EU. It covers 95% of vehicles on Indian roads.'
          }
        },
        {
          '@type': 'Question',
          'name': 'Can I integrate the ConnectIQ APIs into my existing mobile app?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Yes. Unified SDK provides native iOS (Swift), Android (Kotlin), React Native, Flutter, and JavaScript wrappers. Most integrations go live in 3–5 working days.'
          }
        },
        {
          '@type': 'Question',
          'name': 'Is my vehicle data stored securely?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'All data is encrypted in transit (TLS 1.3) and at rest (AES-256). We are ISO 27001 certified and CERT-In compliant. You retain full ownership of your data.'
          }
        },
        {
          '@type': 'Question',
          'name': 'What is the difference between the OBD dongle and the TCU?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'The OBD dongle is self-install and ideal for individual vehicles and consumer use. The TCU is hardwired, fleet-grade, AIS-140 certified, and designed for commercial fleets with advanced I/O requirements.'
          }
        },
        {
          '@type': 'Question',
          'name': 'How does the driver behaviour score work?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Our ML models analyse 40+ telematics events per trip — including speeding, harsh braking, cornering, and night driving — normalised by road type and traffic conditions to produce a fair, context-aware score.'
          }
        },
        {
          '@type': 'Question',
          'name': 'Can Varroc Connect deploy on-premise?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Yes. Our enterprise tier supports on-premise deployment in your private cloud (AWS, GCP, Azure) or on-site data centre. Contact our enterprise team for architecture guidance.'
          }
        }
      ]
    }, 'jsonld-faq');
  }


  scrollTo(sectionId: string) {
    const el = document.getElementById(sectionId);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    this.activeSection = sectionId;
  }

  private setupIntersectionObserver() {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) this.activeSection = entry.target.id;
        });
      },
      { rootMargin: '-30% 0px -60% 0px' }
    );
    this.sections.forEach(s => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
  }
}
